import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { SummaryFigures } from '../../fixedAssetsMeta';

type RegisterSummaryProps = {
    /** `null` while the register is in flight — labels stay, values skeleton. */
    figures: SummaryFigures | null;
};

const Figure: React.FC<{
    label: string;
    /** `null` renders a skeleton in the value's place. */
    value: string | null;
    slot: string;
}> = ({ label, value, slot }) => (
    <div className="flex flex-col gap-1 rounded-xl bg-surface-secondary p-4">
        <span className="text-xs font-medium uppercase tracking-wide text-muted">
            {label}
        </span>
        {value === null ? (
            <Skeleton className="my-1 h-7 w-28 rounded" />
        ) : (
            <span
                className="text-2xl font-semibold tabular-nums text-foreground"
                {...getNodeTestIdProp(`fixed-assets-summary-${slot}`)}
            >
                {value}
            </span>
        )}
    </div>
);

/**
 * The register's four headline figures.
 *
 * All four are computed over **active assets only** and from the same filtered
 * set, so the count and the totals can never disagree with each other: a disposed
 * asset is off the books, and counting five rows while summing six is exactly the
 * inconsistency this screen exists not to ship.
 *
 * While the register loads the four labels stay put and only the values are
 * skeletoned (the repo's loading-state standard), so the cards never reflow.
 */
export const RegisterSummary: React.FC<RegisterSummaryProps> = ({
    figures,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Figure
                label={t('fixedAssets.summary.count')}
                slot="count"
                value={figures ? String(figures.assetCount) : null}
            />
            <Figure
                label={t('fixedAssets.summary.cost')}
                slot="cost"
                value={figures?.totalCostText ?? null}
            />
            <Figure
                label={t('fixedAssets.summary.accumulated')}
                slot="accumulated"
                value={figures?.totalAccumulatedText ?? null}
            />
            <Figure
                label={t('fixedAssets.summary.netBookValue')}
                slot="nbv"
                value={figures?.totalNetBookValueText ?? null}
            />
        </div>
    );
};
